"use client";

import { useState, useMemo, useCallback } from "react";
import Link from "next/link";
import { ArrowRight, BookOpen, Search, ChevronLeft, ChevronRight, ListFilter } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { categoryIcon } from "@/components/blog/category-icons";

export interface BlogCard {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  cover: string | null;
  dateLabel: string;
  tags: string[];
}

const PAGE_SIZE = 9;

/**
 * The searchable "All guides" grid. Renders inside the page's section wrapper
 * (no self-wrapping section/padding). Topic filtering is a single compact
 * dropdown — the visual category browsing lives in the "Browse by topic" rail
 * above, so a full row of filter chips here was redundant.
 */
export function BlogIndexClient({
  posts,
  categoryLabels,
}: {
  posts: BlogCard[];
  /** slug → human label (falls back to the raw tag). */
  categoryLabels?: Record<string, string>;
}) {
  const [query, setQuery] = useState("");
  const [tag, setTag] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const labelFor = (t: string) => categoryLabels?.[t] ?? t;

  const allTags = useMemo(() => {
    const counts = new Map<string, number>();
    posts.forEach((p) => p.tags.forEach((t) => counts.set(t, (counts.get(t) ?? 0) + 1)));
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([t]) => t);
  }, [posts]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return posts.filter((p) => {
      const matchesTag = !tag || p.tags.includes(tag);
      if (!matchesTag) return false;
      if (!q) return true;
      return (
        p.title.toLowerCase().includes(q) ||
        (p.excerpt ?? "").toLowerCase().includes(q) ||
        p.tags.some((t) => t.toLowerCase().includes(q))
      );
    });
  }, [posts, query, tag]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const onSearch = useCallback((v: string) => { setQuery(v); setPage(1); }, []);
  const onTag = useCallback((t: string | null) => { setTag(t); setPage(1); }, []);
  const goToPage = useCallback((p: number) => {
    setPage(p);
    if (typeof document !== "undefined") {
      document.getElementById("blog-results")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  return (
    <div>
      {/* Search + a single compact topic filter (no chip row) */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={query}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search guides…"
            className="h-12 rounded-xl pl-12 pr-20"
            aria-label="Search blog guides"
          />
          {query && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 min-h-11 min-w-11 sm:min-h-0 sm:min-w-0 text-muted-foreground hover:text-foreground"
            >
              Clear
            </Button>
          )}
        </div>

        {allTags.length > 0 && (
          <div className="relative sm:w-60">
            <ListFilter className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <select
              value={tag ?? ""}
              onChange={(e) => onTag(e.target.value || null)}
              aria-label="Filter guides by topic"
              className="h-12 w-full appearance-none rounded-xl border border-input bg-background pl-10 pr-9 text-sm font-medium text-foreground outline-hidden transition-colors focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">All topics</option>
              {allTags.map((t) => (
                <option key={t} value={t}>{labelFor(t)}</option>
              ))}
            </select>
            <ChevronRight className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 rotate-90 text-muted-foreground" />
          </div>
        )}
      </div>

      {/* Results */}
      <div id="blog-results" className="mt-8 scroll-mt-24">
        {(query || tag) && (
          <p className="mb-6 text-sm text-muted-foreground" aria-live="polite">
            {filtered.length} {filtered.length === 1 ? "guide" : "guides"}
            {tag ? <> in <span className="font-medium text-foreground">{labelFor(tag)}</span></> : null}
            {query ? <> matching &ldquo;{query}&rdquo;</> : null}
          </p>
        )}

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-center">
            <BookOpen className="h-10 w-10 text-muted-foreground/40" />
            <p className="mt-4 font-medium text-foreground">No guides found</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Try a different search or{" "}
              <button
                type="button"
                onClick={() => { onSearch(""); onTag(null); }}
                className="rounded text-primary underline underline-offset-2"
              >
                clear the filters
              </button>
              .
            </p>
          </div>
        ) : (
          <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {pageItems.map((p) => {
              const Icon = p.tags[0] ? categoryIcon(p.tags[0]) : null;
              return (
                <li key={p.id}>
                  <Link
                    href={`/blog/${p.slug}`}
                    className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border/60 bg-card transition-all hover:border-primary/40 hover:shadow-lg"
                  >
                    <div className="relative aspect-4/3 w-full overflow-hidden bg-muted">
                      {p.cover ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={p.cover}
                          alt={p.title}
                          loading="lazy"
                          className="absolute inset-0 h-full w-full object-cover object-[center_70%] transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center">
                          <BookOpen className="h-8 w-8 text-muted-foreground/30" />
                        </div>
                      )}
                    </div>
                    <div className="flex flex-1 flex-col p-5">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        {Icon && p.tags[0] && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                            <Icon className="h-3 w-3" aria-hidden="true" />
                            {labelFor(p.tags[0])}
                          </span>
                        )}
                        {p.dateLabel && (
                          <p className="text-xs uppercase tracking-wide text-muted-foreground">{p.dateLabel}</p>
                        )}
                      </div>
                      <h2 className="mt-2 heading-card group-hover:text-primary">{p.title}</h2>
                      {p.excerpt && <p className="mt-2 line-clamp-3 flex-1 text-sm text-muted-foreground">{p.excerpt}</p>}
                      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary">
                        Read more <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                      </span>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <nav className="mt-12 flex items-center justify-center gap-2" aria-label="Blog pagination">
            <Button type="button" variant="outline" size="sm" onClick={() => goToPage(safePage - 1)} disabled={safePage <= 1} className="min-h-11" aria-label="Previous page">
              <ChevronLeft className="h-4 w-4" /> Prev
            </Button>
            <div className="flex flex-wrap items-center justify-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => goToPage(n)}
                  aria-current={n === safePage ? "page" : undefined}
                  className={`inline-flex h-11 min-w-11 items-center justify-center rounded-lg px-3 text-sm font-medium transition-colors ${
                    n === safePage ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => goToPage(safePage + 1)} disabled={safePage >= totalPages} className="min-h-11" aria-label="Next page">
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </nav>
        )}
      </div>
    </div>
  );
}
